import React, { useState } from "react";
import {
  IconFileText,
  IconPlus,
  IconSearch,
  IconDownload,
  IconTrash,
  IconCalendar,
  IconShieldCheck,
  IconFileCertificate,
  IconAlertCircle,
  IconPaperclip
} from "@tabler/icons-react";
import {
  useAssetDocuments,
  useAssets,
  addAssetDocument,
  deleteAssetDocument
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetDocumentsTab() {
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: documents, error, isLoading, mutate } = useAssetDocuments(selectedAssetId || null);
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    document_type: "Warranty Certificate",
    document_title: "",
    file_url: "",
    file_size_kb: 250,
    file_type: "application/pdf",
    expiry_date: "",
    notes: ""
  });

  const filteredDocs = Array.isArray(documents)
    ? documents.filter((d) => {
        if (docTypeFilter !== "all" && d.document_type !== docTypeFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          d.document_title?.toLowerCase().includes(q) ||
          d.asset_code?.toLowerCase().includes(q) ||
          d.asset_name?.toLowerCase().includes(q) ||
          d.document_type?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenModal = () => {
    setFormData({
      asset_id: selectedAssetId || assets?.[0]?.id || "",
      document_type: "Warranty Certificate",
      document_title: "",
      file_url: "",
      file_size_kb: 180,
      file_type: "application/pdf",
      expiry_date: "",
      notes: ""
    });
    setIsModalOpen(true);
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset");
      return;
    }
    if (!formData.document_title) {
      toast.error("Please provide document title");
      return;
    }

    try {
      await addAssetDocument(formData);
      toast.success("Document added to asset vault!");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to add document");
    }
  };

  const handleDelete = async (docId, title) => {
    if (!window.confirm(`Delete document "${title}"?`)) return;

    try {
      await deleteAssetDocument(docId);
      toast.success("Document deleted");
      mutate();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diffDays = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
    return diffDays > 0 && diffDays <= 30;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconFileText className="w-6 h-6 text-restro-green" />
            Asset Document Vault & Warranty Compliance
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Store supplier invoices, user manuals, AMC service contracts, insurance policies, and calibration records.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedAssetId}
            onChange={(e) => setSelectedAssetId(e.target.value)}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
          >
            <option value="">All Assets</option>
            {assets?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.asset_code} - {a.asset_name}
              </option>
            ))}
          </select>

          <select
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
          >
            <option value="all">All Document Types</option>
            <option value="Warranty Certificate">Warranty Certificate</option>
            <option value="AMC / Maintenance Contract">AMC Contract</option>
            <option value="User Manual">User Manual / SOP</option>
            <option value="Purchase Invoice">Purchase Invoice</option>
            <option value="Insurance Policy">Insurance Policy</option>
            <option value="Calibration Certificate">Calibration Certificate</option>
          </select>

          <button
            onClick={handleOpenModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Documents Grid / Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Document Title</th>
                <th className="px-6 py-4">Asset</th>
                <th className="px-6 py-4">Document Type</th>
                <th className="px-6 py-4">Expiration / Renewal</th>
                <th className="px-6 py-4">Uploaded Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-gray-400">
                    Loading documents...
                  </td>
                </tr>
              ) : filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-400">
                    <IconFileText className="w-12 h-12 mx-auto mb-2 opacity-30 text-restro-green" />
                    No documents uploaded in vault yet.
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc) => {
                  const expiring = isExpiringSoon(doc.expiry_date);
                  const expired = isExpired(doc.expiry_date);
                  return (
                    <tr key={doc.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          <IconPaperclip className="w-4 h-4 text-gray-400" />
                          {doc.document_title}
                        </div>
                        {doc.notes && <div className="text-xs text-gray-400 mt-0.5">{doc.notes}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {doc.asset_name || "Asset #" + doc.asset_id}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          {doc.asset_code || `AST-${doc.asset_id}`}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                          {doc.document_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {doc.expiry_date ? (
                          <div>
                            <div
                              className={`text-xs font-semibold ${
                                expired
                                  ? "text-rose-600 dark:text-rose-400"
                                  : expiring
                                  ? "text-amber-600 dark:text-amber-400"
                                  : "text-gray-800 dark:text-gray-200"
                              }`}
                            >
                              {new Date(doc.expiry_date).toLocaleDateString()}
                            </div>
                            {expired ? (
                              <span className="text-[10px] text-rose-500 font-bold">EXPIRED</span>
                            ) : expiring ? (
                              <span className="text-[10px] text-amber-500 font-bold">RENEW SOON</span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No Expiry</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {doc.file_url ? (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-restro-green hover:underline"
                          >
                            <IconDownload className="w-3.5 h-3.5" />
                            View / Download
                          </a>
                        ) : null}
                        <button
                          onClick={() => handleDelete(doc.id, doc.document_title)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg transition"
                          title="Delete Document"
                        >
                          <IconTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Upload Document */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconFileText className="w-5 h-5 text-restro-green" />
                Add Asset Document
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleAddDocument} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Target Asset *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Document Title *
                </label>
                <input
                  type="text"
                  placeholder="e.g. 2-Year Compressor Warranty Card"
                  value={formData.document_title}
                  onChange={(e) => setFormData({ ...formData, document_title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Document Category *
                  </label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="Warranty Certificate">Warranty Certificate</option>
                    <option value="AMC / Maintenance Contract">AMC Contract</option>
                    <option value="Purchase Invoice">Purchase Invoice / Bill</option>
                    <option value="User Manual">User Manual / SOP</option>
                    <option value="Insurance Policy">Insurance Policy</option>
                    <option value="Calibration Certificate">Calibration Certificate</option>
                    <option value="Inspection Report">Inspection Report</option>
                    <option value="Disposal Certificate">Disposal Certificate</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Expiry / Renewal Date
                  </label>
                  <input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  File Attachment URL / Local Storage Path
                </label>
                <input
                  type="text"
                  placeholder="https://... or /uploads/assets/..."
                  value={formData.file_url}
                  onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Notes / Clause Details
                </label>
                <textarea
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Terms, coverage limitations, service hotline numbers..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Save to Vault
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
